import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { openDialogAction } from "@icarius-common/dialog/actions";
import {
    getIsLoading,
    getGridData,
    getFormData,
    getFields,
    getProcess,
    getDateFormat,
    getAddCodemps,
    getRestrictions,
} from "../selectors";
import {
    createTableFormAction,
    updateTableFormAction,
    deleteTableFormAction,
    downloadTableExcelAction,
    changeCheckTableFormAction,
} from "../actions";
import { isRecordInBlockedList } from "../restrictionsUtils";
import { createAggridColumn } from "@icarius-table/utils";
import { createNumberFormatter } from "@icarius-table/number";
import { getUserRole } from "@icarius-app/selectors";
import { useHistory } from "react-router-dom";
import paths from "@icarius-router/paths";
import { getLocalizedString } from "@icarius-localization/strings";

const useSystemTablesForm = (isSystemTables, tableCode, codver, detailTable, masterRowInternalCode) => {
    const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
    const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [uploadFileDialogIsOpen, setUploadFileDialogIsOpen] = useState(false);

    const dispatch = useDispatch();
    const history = useHistory();
    const level = useSelector(getUserRole);
    const addCodemps = useSelector(getAddCodemps);
    const gridData = useSelector(getGridData);
    const formData = useSelector(getFormData);
    const fields = useSelector(getFields);
    const isLoading = useSelector(getIsLoading);
    const process = useSelector(getProcess);
    const dateFormat = useSelector(getDateFormat);
    const restrictions = useSelector(getRestrictions);

    const handleOpenDeleteDialog = (gridRef) => {
        let rows = gridRef.api.getSelectedRows();
        if (rows.length < 1) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRecord"), severity: "warning" })
            );
            return;
        }

        if (rows.some((row) => isRecordInBlockedList(row, restrictions?.blockedRecords))) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("cannotModifyOrDeleteBlockedRecords"),
                    severity: "warning",
                })
            );
            return;
        }

        const hasCheck = Object.hasOwn(rows[0], "Status de visación");

        if ((level === "C" || level === "M") && hasCheck) {
            if (!rows.every((item) => item["Status de visación"] === "Ingresado")) {
                dispatch(
                    openSnackbarAction({
                        msg: getLocalizedString("onlyTransactionsWithStatusEnteredCanBeDeleted"),
                        severity: "warning",
                    })
                );
                return;
            }
        }

        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("deleteRowsConfirmation"),
                onConfirm: () => handleDelete(rows),
            })
        );
    };

    const handleOpenCreateDialog = () => {
        setSelectedData(null);
        setCreateDialogIsOpen(true);
    };

    const handleOpenEditDialog = (gridRef) => {
        let rows = gridRef.api.getSelectedRows();
        if (rows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        setSelectedData(formData.find((item) => item.internalCode === rows[0].internalCode));
        setEditDialogIsOpen(true);
    };

    const handleCloseCreateEditDialog = () => {
        setCreateDialogIsOpen(false);
        setEditDialogIsOpen(false);
        setSelectedData(null);
    };

    const handleDelete = (rows) => {
        const unblockedRows = rows.filter((row) => !isRecordInBlockedList(row, restrictions?.blockedRecords));
        if (unblockedRows.length === 0) return;
        const codes = unblockedRows.map((item) => item.internalCode);
        dispatch(deleteTableFormAction(isSystemTables, { tableCode, codver, codes, masterRowInternalCode }));
    };

    const hasAggridColumnWithRenderer = useMemo(() => {
        const fieldsWithRenderer = Object.values(fields)?.filter((fieldItem) =>
            ["LK", "LI", "LF", "GC", "IU", "AU"].includes(fieldItem.columnTypeInGrid)
        );

        return Boolean(fieldsWithRenderer?.length > 0);
    }, [fields]);

    const columnDefs = useMemo(() => {
        let columnDefs = [
            {
                headerName: getLocalizedString("internalCode"),
                field: "internalCode",
                filter: "agTextColumnFilter",
                checkboxSelection: function(params) {
                    return params.columnApi.getRowGroupColumns().length === 0;
                },
                headerCheckboxSelection: function(params) {
                    return params.columnApi.getRowGroupColumns().length === 0;
                },
                headerCheckboxSelectionFilteredOnly: true,
                enableRowGroup: true,
                maxWidth: 250,
            },
        ];

        if (Object.keys(fields)?.length > 0) {
            const orderedFields = Object.values(fields).sort((a, b) => a.order - b.order);

            orderedFields.forEach((fieldItem) => {
                if (fieldItem && fieldItem.columnTypeInGrid !== "-") {
                    let item = createAggridColumn(fieldItem.name, fieldItem.columnTypeInGrid);
                    item.maxWidth = 250;

                    if (fieldItem.columnTypeInGrid === "Num") {
                        item.valueFormatter = createNumberFormatter(0, 0);
                    }

                    if (fieldItem.columnTypeInGrid === "2dec") {
                        item.valueFormatter = createNumberFormatter(2, 2);
                    }

                    if (fieldItem.columnTypeInGrid === "3dec") {
                        item.valueFormatter = createNumberFormatter(3, 3);
                    }

                    if (fieldItem.columnTypeInGrid === "4dec") {
                        item.valueFormatter = createNumberFormatter(4, 4);
                    }

                    if (fieldItem.columnTypeInGrid === "5dec") {
                        item.valueFormatter = createNumberFormatter(5, 5);
                    }

                    if (fieldItem.columnTypeInGrid === "Mon") {
                        item.valueFormatter = createNumberFormatter(3, 3);
                    }

                    if (fieldItem.columnTypeInGrid === "LK") {
                        item.cellRenderer = "LinkRenderer";
                    }

                    if (fieldItem.columnTypeInGrid === "LF") {
                        item.cellRenderer = "ExternalFileRenderer";
                    }

                    if (fieldItem.columnTypeInGrid === "LI") {
                        item.cellRenderer = "ExternalImageRenderer";
                    }

                    if (fieldItem.columnTypeInGrid === "GC") {
                        item.cellRenderer = "CoordinatesRenderer";
                    }

                    if (fieldItem.columnTypeInGrid === "IU") {
                        item.cellRenderer = "ImageRenderer";
                    }

                    if (fieldItem.columnTypeInGrid === "AU") {
                        item.cellRenderer = "FileRenderer";
                    }

                    columnDefs.push(item);
                }
            });
        }
        return columnDefs;
    }, [fields]);

    const handleDownloadStructure = (gridRef) => {
        let filteredRows = [];
        let internalCodes = [];

        gridRef.api.forEachNodeAfterFilter((row) => {
            filteredRows.push(row.data);
        });

        internalCodes = filteredRows.map((item) => item.internalCode);

        dispatch(downloadTableExcelAction(isSystemTables, { internalCodes, tableCode, codver, masterRowInternalCode }));
    };

    const handleOpenUploadFileDialog = () => {
        setUploadFileDialogIsOpen(true);
    };

    const handleCloseUploadFileDialog = () => {
        setUploadFileDialogIsOpen(false);
    };

    const handleCreate = (dataToSend) => {
        dispatch(
            createTableFormAction(isSystemTables, { ...dataToSend, tableCode, codver, masterRowInternalCode })
        ).then((res) => res?.status === 200 && handleCloseCreateEditDialog());
    };

    const handleModify = (dataToSend) => {
        if (isRecordInBlockedList(dataToSend, restrictions?.blockedRecords)) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("cannotModifyOrDeleteBlockedRecords"),
                    severity: "warning",
                })
            );
            return;
        }
        dispatch(
            updateTableFormAction(isSystemTables, { ...dataToSend, tableCode, codver, masterRowInternalCode })
        ).then((res) => res?.status === 200 && handleCloseCreateEditDialog());
    };

    const handleChangeCheck = (data, newStatus) => {
        if (isRecordInBlockedList(data, restrictions?.blockedRecords)) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("cannotModifyOrDeleteBlockedRecords"),
                    severity: "warning",
                })
            );
            return;
        }
        dispatch(
            changeCheckTableFormAction(isSystemTables, {
                masterRowInternalCode,
                internalCode: data.internalCode,
                code: data.code,
                codver: data.codver,
                vis: newStatus,
            })
        ).then((res) => res?.status === 200 && handleCloseCreateEditDialog());
    };

    const handleOpenEditDialogRowClick = (row) => {
        setSelectedData(formData.find((item) => item.internalCode === row.internalCode));
        setEditDialogIsOpen(true);
    };

    const handleOpenDetailTableRowClick = (row) => {
        const searchArray = [
            `type=${isSystemTables ? "S" : "U"}`,
            `code=${detailTable?.tableCode}`,
            `codver=${detailTable?.codver}`,
            `masterRowInternalCode=${row.internalCode}`,
            `codeMasterTable=${tableCode}`,
            `codverMasterTable=${codver}`,
            "hideGoBack=N",
        ];

        history.push({
            path: isSystemTables ? paths.systemTables : paths.userTables,
            search: searchArray.join("&"),
        });
    };

    const handleOpenDetailTableByButton = (gridRef) => {
        let rows = gridRef.api.getSelectedRows();
        if (rows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        handleOpenDetailTableRowClick(rows[0]);
    };

    const state = {
        editDialogIsOpen,
        createDialogIsOpen,
        selectedData,
        uploadFileDialogIsOpen,
    };

    const handlers = {
        handleCreate,
        handleModify,
        handleChangeCheck,
        handleOpenDeleteDialog,
        handleOpenCreateDialog,
        handleOpenEditDialogRowClick,
        handleOpenEditDialog,
        handleCloseCreateEditDialog,
        handleDownloadStructure,
        handleOpenUploadFileDialog,
        handleCloseUploadFileDialog,
        handleOpenDetailTableByButton,
        handleOpenDetailTableRowClick,
    };

    return {
        level,
        dateFormat,
        process,
        addCodemps,
        gridData,
        detailTable,
        columnDefs,
        hasAggridColumnWithRenderer,
        fields,
        isLoading,
        restrictions,
        state,
        handlers,
    };
};

export default useSystemTablesForm;
